// ============================================================================
// ASSESSMENT SUMMARY - AUDITOR PAGE
// ============================================================================
// Purpose: Provide comprehensive assessment reports and summaries
// Mental model: "Show me the big picture of compliance health."
// ============================================================================

import React, { useState, useEffect } from 'react';
import { auditApi, AssessmentSummary as AssessmentSummaryType } from '../../audit/api';
import PageInfoButton from '@/components/PageInfoButton';

const AssessmentSummary: React.FC = () => {
  const [assessments, setAssessments] = useState<AssessmentSummaryType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssessment, setSelectedAssessment] = useState<AssessmentSummaryType | null>(null);

  useEffect(() => {
    loadAssessments();
  }, []);

  const loadAssessments = async () => {
    try {
      const assessmentData = await auditApi.getAssessmentSummaries();
      setAssessments(assessmentData);
    } catch (error) {
      console.error('Failed to load assessments:', error);
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-yellow-600 bg-yellow-100';
      case 'draft': return 'text-blue-600 bg-blue-100';
      case 'reviewed': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleViewDetails = (assessment: AssessmentSummaryType) => {
    setSelectedAssessment(assessment);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Assessment Summary</h1><PageInfoButton /></div>
        <p className="text-slate-600 mt-1">
          Comprehensive overview of compliance assessments and findings
        </p>
      </div>

      {/* Assessment Cards */}
      <div className="space-y-4">
        {assessments.map((assessment) => (
          <div key={assessment.id} className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-semibold text-slate-900">{assessment.company_name}</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(assessment.status)}`}>
                    {assessment.status}
                  </span>
                </div>
                <p className="text-slate-600 mb-4">{assessment.framework_name}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${getScoreColor(assessment.overall_score)}`}>
                      {assessment.overall_score}%
                    </div>
                    <div className="text-sm text-slate-500">Overall Score</div>
                  </div>

                  <div className="text-center">
                    <div className="text-3xl font-bold text-slate-900">
                      {new Date(assessment.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-sm text-slate-500">Assessment Date</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(assessment.status)}`}>
                      {assessment.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Last Updated</p>
                    <p className="font-medium text-slate-900">{new Date(assessment.updated_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => handleViewDetails(assessment)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                View Full Report
              </button>
              <button className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">
                Export PDF
              </button>
              <button className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">
                Send to Client
              </button>
            </div>
          </div>
        ))}
      </div>

      {assessments.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12">
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="material-icons-outlined text-slate-400">assessment</span>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Assessments Available</h3>
          <p className="text-slate-600">Assessment summaries will appear here once audits are completed.</p>
        </div>
      )}

      {/* Assessment Detail Modal */}
      {selectedAssessment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-slate-900">Assessment Report</h2>
                <button
                  onClick={() => setSelectedAssessment(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <span className="material-icons-outlined">close</span>
                </button>
              </div>

              <div className="space-y-6">
                <div className="border-b border-slate-100 pb-4">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{selectedAssessment.company_name}</h3>
                  <p className="text-slate-600">{selectedAssessment.framework_name}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-sm text-slate-500">Date: {new Date(selectedAssessment.created_at).toLocaleDateString()}</span>
                    <span className="text-sm text-slate-500">Status: {selectedAssessment.status}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-2">Overall Compliance</h4>
                    <div className={`text-3xl font-bold ${getScoreColor(selectedAssessment.overall_score)}`}>
                      {selectedAssessment.overall_score}%
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Compliance Score</p>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-2">Assessment Status</h4>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedAssessment.status)}`}>
                      {selectedAssessment.status}
                    </span>
                    <p className="text-sm text-slate-600 mt-1">Current Status</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    Download Full Report
                  </button>
                  <button className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">
                    Email Report
                  </button>
                  <button
                    onClick={() => setSelectedAssessment(null)}
                    className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentSummary;